import log from '@/lib/logger';
import {useEffect, useState} from 'react';
import {Dialog, DialogActions, DialogDescription, DialogTitle} from "@/components/tailwind/dialog";
import {Input} from "@/components/tailwind/input";
import {Button} from "@/components/tailwind/button";
import {useForm} from "react-hook-form";
import {zodResolver} from "@hookform/resolvers/zod";
import * as z from "zod";
import {ServiceGroupWithServices} from "@/models/service";
import {ValidationIssue} from "@/types/problem-details.ts";
import {deleteServiceGroup} from '@/services/service-services';
import {useAuth} from "@/context/use-auth.tsx";
import {useServicesTranslation, useCommonTranslation} from "@/i18n/hooks";
import {useToast} from "@/hooks/use-toast.ts";
import ValidationAlert from "@/components/validation-alert.tsx";
import {Description, Field} from "@/components/tailwind/fieldset.tsx";

type DeleteServiceGroupDialogProps = {
    isOpen: boolean;
    onClose: () => void;
    serviceGroup: ServiceGroupWithServices | undefined;
    onConfirm: () => void;
};

type FormValues = {
    confirmation: string;
};

export function DeleteServiceGroupDialog({isOpen, onClose, serviceGroup, onConfirm}: DeleteServiceGroupDialogProps) {
    const serviceCount = serviceGroup?.services?.length || 0;

    const schema = z.object({
        confirmation: z
            .string()
            .refine((val) => val === serviceCount.toString(), {
                message: "invalid-confirmation-value",
            }),
    });

    const {
        register,
        handleSubmit,
        reset,
        setError,
        formState: {errors, isValid},
    } = useForm<FormValues>({
        resolver: zodResolver(schema),
        mode: "onChange",
    });

    const {t} = useServicesTranslation();
    const {t: tCommon} = useCommonTranslation();
    const {token} = useAuth();
    const {toast} = useToast()

    useEffect(() => {
        if (isOpen) {
            reset();
        }
    }, [isOpen, reset]);

    function showSuccessToast() {
        toast({
            title: t("deleteServiceGroupDialog.toast.success.title"),
            description: t(
                "deleteServiceGroupDialog.toast.success.description",
                {
                    count: serviceCount,
                    groupName: serviceGroup!.name,
                }
            ),
            variant: "default",
        })

    }

    async function onSubmit(_: FormValues): Promise<void> {
        if (!serviceGroup) return;

        if (await doServiceGroupDeletion()) {
            onConfirm()
            showSuccessToast()
            onClose()
        }
    }

    async function doServiceGroupDeletion(): Promise<boolean> {
        if (!serviceGroup) {
            return false;
        }

        try {
            await deleteServiceGroup(token, serviceGroup!.id)
            return true
        } catch (error: any) {
            log.error(`An error occurred while trying to delete service group ${serviceGroup!.id}.`, error);

            if (error.validationIssues) {
                error.validationIssues.map(
                    (finding: ValidationIssue) => {
                        setError(
                            finding.field as "confirmation" | `root.${string}` | "root",
                            {
                                message: finding.message
                            }
                        )
                    }
                )
            }

            if (error.status) {
                setError("root", {
                    message: tCommon("serverError.response-code-with-details", {
                        statusCode: error.status,
                        errorDetails: error.error
                    })
                })
            }

            return false;
        }
    }

    return (
        <Dialog open={isOpen} onClose={onClose}>
            <form onSubmit={handleSubmit(onSubmit)}>
                <DialogTitle>{t("deleteServiceGroupDialog.title", {ns: "services"})}</DialogTitle>

                {errors.root && (
                    <ValidationAlert message={tCommon(errors.root.message as any || "serverError.unknown" as any)} className="mt-4"/>
                )}

                <DialogDescription>
                    <div dangerouslySetInnerHTML={{
                        __html: t("deleteServiceGroupDialog.description", {ns: "services", count: serviceCount, groupName: serviceGroup?.name})
                    }} />
                </DialogDescription>

                <div className="space-y-4">
                        <Field>
                            <Description>
                                <div dangerouslySetInnerHTML={{
                                    __html: t("deleteServiceGroupDialog.confirmationField.description", {ns: "services", count: serviceCount})
                                }} />
                            </Description>
                            <Input
                                {...register("confirmation")}
                                type="text"
                                placeholder={t("deleteServiceGroupDialog.confirmationField.placeholder" as any)}
                                aria-label={t("deleteServiceGroupDialog.confirmationField.ariaLabel" as any)}
                                invalid={!!errors.confirmation}
                                data-testid="service-count-input"
                            />
                            {errors.confirmation && (
                                <ValidationAlert message={
                                    t(
                                        errors.confirmation.message as any || "unknown-confirmation-issue" as any,
                                        {
                                            serviceCount: serviceCount
                                        }
                                    )
                                }/>
                            )}
                        </Field>
                    </div>

                    <DialogActions>
                        <Button
                            plain
                            onClick={onClose}
                            aria-label={t("deleteServiceGroupDialog.buttons.cancel.ariaLabel" as any)}
                        >
                            {t("deleteServiceGroupDialog.buttons.cancel.text", {ns: "services"})}
                        </Button>
                        <Button
                            type="submit"
                            color="red"
                            aria-label={t("deleteServiceGroupDialog.buttons.submit.ariaLabel" as any)}
                            disabled={!isValid}
                            data-testid="delete-confirm-button"
                        >
                            {t("deleteServiceGroupDialog.buttons.submit.text", {ns: "services"})}
                        </Button>
                    </DialogActions>
            </form>
        </Dialog>
    );
}

export function useDeleteServiceGroupDialog() {
    const [isOpen, setIsOpen] = useState(false);
    const [serviceGroup, setServiceGroup] = useState<ServiceGroupWithServices | undefined>(undefined)

    function openDialog(serviceGroup?: ServiceGroupWithServices | undefined) {
        setServiceGroup(serviceGroup)
        setIsOpen(true)
    }

    function closeDialog() {
        setIsOpen(false)
    }

    return {
        isDeleteServiceGroupDialogOpen: isOpen,
        initialServiceDialogData: serviceGroup,
        openDeleteServiceGroupDialog: openDialog,
        closeDeleteServiceGroupDialog: closeDialog,
        serviceGroup,
        deleteServiceGroupDialogProps: {
            isOpen: isOpen,
            onClose: closeDialog,
            serviceGroup: serviceGroup,
        }
    }
}
